import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  Media,
  MediaPickerComponent,
  MediaType,
  Severity,
} from '@gripp/shared-logic';
import { useState } from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { Text } from 'react-native-paper';
import { array, mixed, string, object as yupObject } from 'yup';
import { useAutoFocusRef } from '../../hooks';
import { Spacing } from '../../styles';
import { Colors } from '../../themes';
import { ControlledTextInput } from '../forms';
import { KeyboardSafeView } from '../keyboard/keyboardSafeView';
import { MediaGalleryComponent } from '../media';
import { MentionInput } from '../mentionInputField';
import { AdjustCreationDateField } from './adjustCreationDateField';
import { LabelSelector } from './labelSelector';
import { QuickReminderField } from './quickReminderField';

export const NewIssueReportFormSchema = yupObject({
  message: string().required(),
  title: string().optional(),
  activityType: mixed<string>()
    .oneOf(['red', 'yellow', 'green', 'purple'])
    .required(),
  quickReminderDate: mixed().optional(),
  changeDisplayDate: mixed().optional(),
  adjustCreationDate: mixed().optional(),
  labels: array()
    .of(
      yupObject({
        id: string().required(),
        name: string().required(),
      })
    )
    .optional(),
});

export type NewIssueReportFormProps = {
  workspaceId?: string;
  mediaGallery: MediaGalleryComponent;
  mediaPicker: MediaPickerComponent;
  acceptedMediaTypes?: MediaType[];
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  addMedia: (media: Media) => void;
  setIsUploading?: (isUploading: boolean) => void;
  availableLabels?: Array<{ id: string; name: string }>;
  onCreateLabel?: (name: string) => Promise<{ id: string; name: string }>;
  watch: any;
  hideAdvancedSettings?: boolean;
};

export const NewIssueReportForm = (props: NewIssueReportFormProps) => {
  const { t } = useTranslation();
  const MediaPicker = props.mediaPicker;
  const MediaGallery = props.mediaGallery;
  const [isDescriptionFocused, setIsDescriptionFocused] = useState(false);

  const isWeb = Platform.OS === 'web';

  const elementFocusCallBack = useAutoFocusRef();

  const getActivityTypeLabel = (severity: string) => {
    switch (severity) {
      case Severity.Red:
        return t('activity.filter.urgent');
      case Severity.Yellow:
        return t('activity.filter.pending');
      case Severity.Green:
        return t('activity.filter.solved');
      case Severity.Purple:
        return t('activity.filter.notes');
      default:
        return '';
    }
  };

  const buttonOptions = [
    {
      label: getActivityTypeLabel(Severity.Red),
      value: Severity.Red,
      icon: {
        style: {
          backgroundColor: Colors.red,
        },
        name: 'flag', // Flag icon for Urgent
      },
    },
    {
      label: getActivityTypeLabel(Severity.Yellow),
      value: Severity.Yellow,
      icon: {
        style: {
          backgroundColor: Colors.yellow,
        },
        name: 'dots-horizontal-circle', // Three dots for Pending
      },
    },
    {
      label: getActivityTypeLabel(Severity.Green),
      value: Severity.Green,
      icon: {
        style: {
          backgroundColor: Colors.green,
        },
        name: 'check-circle', // Check circle for Solved (was correct before)
      },
    },
    {
      label: getActivityTypeLabel(Severity.Purple),
      value: Severity.Purple,
      icon: {
        style: {
          backgroundColor: Colors.purple,
        },
        name: 'note-plus', // Document with plus for Note
      },
    },
  ];

  const renderFormContent = () => (
    <>
      <View style={{ ...styles.formItem, zIndex: 2 }}>
        <View>
          <Text style={styles.formItemTitle}>
            {t('issue.common.description')}
          </Text>
        </View>
        <Controller
          control={props.control}
          name="message"
          render={({ field: { onChange, value } }) => (
            <View
              style={[
                styles.mentionInputContainer,
                {
                  borderColor: isDescriptionFocused
                    ? Colors.black
                    : Colors.tabInactive,
                },
              ]}
            >
              <MentionInput
                inputFieldText={value || ''}
                setInputFieldText={onChange}
                setIsMultiline={() => {}}
                inputHeight={100}
                setInputHeight={() => {}}
                send={() => {}}
                suggestionsPosition="bottom"
                handleEnter={false}
              />
            </View>
          )}
        />
      </View>
      <View style={styles.formItem}>
        <Text style={styles.formItemTitle}>{t('issue.common.addPicture')}</Text>
        <MediaGallery
          onMediaAdded={props.addMedia}
          mediaPicker={MediaPicker}
          onUploadingChanged={props.setIsUploading}
          workspaceId={props.workspaceId}
          acceptedMediaTypes={props.acceptedMediaTypes}
        />
      </View>
      <View style={styles.formItem}>
        <View>
          <Text style={styles.formItemTitle}>
            {t('activity.filter.activityTypes')}
          </Text>
        </View>
        <Controller
          control={props.control}
          name="activityType"
          render={({ field: { onChange, value } }) => (
            <View style={styles.activityTypeGrid}>
              {buttonOptions.map((option) => (
                <TouchableOpacity
                  key={option.value}
                  style={styles.activityTypeButtonContainer}
                  onPress={() => onChange(option.value)}
                >
                  <View style={styles.activityTypeButtonWrapper}>
                    {value === option.value && (
                      <View
                        style={[
                          styles.activityTypeButtonOutline,
                          { borderColor: Colors[option.value] },
                        ]}
                      />
                    )}
                    <View
                      style={[
                        styles.activityTypeButton,
                        { backgroundColor: Colors[option.value] },
                      ]}
                    >
                      <MaterialCommunityIcons
                        name={option.icon.name as any}
                        size={24}
                        color={Colors.white}
                      />
                    </View>
                  </View>
                  <Text
                    style={[
                      styles.activityTypeButtonText,
                      value === option.value
                        ? styles.activityTypeButtonTextSelected
                        : styles.activityTypeButtonTextDefault,
                    ]}
                  >
                    {option.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        />
      </View>
      <View style={styles.formItem}>
        <View>
          <Text style={styles.formItemTitle}>{t('issue.new.title')}</Text>
        </View>
        <ControlledTextInput
          control={props.control}
          name="title"
          height={44}
          placeholder={t('issue.new.optionalPlaceholder')}
          disableInnerStyle={false}
          outlinedTextInput
        />
      </View>
      {!props.hideAdvancedSettings && (
        <>
          <View style={styles.subheaderContainer}>
            <View style={styles.subheaderLine} />
            <Text style={styles.advancedSettingsTitle}>
              {t('issue.advancedSettings')}
            </Text>
            <View style={styles.subheaderLine} />
          </View>

          {props.availableLabels && props.onCreateLabel && (
            <View style={styles.formItem}>
              <LabelSelector
                selectedLabels={props.watch('labels') || []}
                availableLabels={props.availableLabels}
                onLabelsChange={(labels) => {
                  props.setValue('labels', labels);
                }}
                onCreateLabel={props.onCreateLabel}
              />
            </View>
          )}
          <View style={styles.formItem}>
            <QuickReminderField
              control={props.control}
              setValue={props.setValue}
            />
          </View>
          <View style={styles.formItem}>
            <AdjustCreationDateField
              control={props.control}
              setValue={props.setValue}
            />
          </View>
        </>
      )}
    </>
  );

  return (
    <KeyboardSafeView style={styles.keyboardContainer}>
      {isWeb ? (
        <ScrollView
          contentContainerStyle={styles.formContainer}
          style={styles.webScrollView}
          showsVerticalScrollIndicator={true}
          scrollEnabled={true}
        >
          {renderFormContent()}
        </ScrollView>
      ) : (
        <ScrollView
          contentContainerStyle={styles.formContainer}
          style={{ borderRadius: 12 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={true}
          nestedScrollEnabled={true}
          scrollEnabled={true}
          automaticallyAdjustKeyboardInsets={true}
          keyboardDismissMode="interactive"
        >
          {renderFormContent()}
        </ScrollView>
      )}
    </KeyboardSafeView>
  );
};

const styles = StyleSheet.create({
  keyboardContainer: {
    flex: 1,
  },
  webScrollView: {
    flex: 1,
  },
  formContainer: {
    gap: 8,
    paddingBottom: 400, // Increased padding to ensure date picker doesn't hide the field
    paddingTop: 16, // Add top padding for better spacing
    flexGrow: 1, // Allow container to expand
  },
  formItemTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginBottom: 16,
  },
  formItem: {
    paddingHorizontal: Spacing.basePadding.paddingHorizontal,
    paddingVertical: Spacing.basePadding.paddingVertical,
    backgroundColor: Colors.white,
    marginBottom: 4, // Add small margin between form items
  },
  subheaderContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 16,
    marginBottom: 16,
  },
  subheaderLine: {
    flex: 1,
    height: 1,
    backgroundColor: Colors.graySpacer,
  },
  subheaderTitle: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 16,
    marginHorizontal: 10,
  },
  advancedSettingsTitle: {
    color: Colors.grayText,
    fontWeight: '400',
    lineHeight: 18,
    marginHorizontal: 10,
    fontSize: 14,
  },
  activityTypeGrid: {
    flexDirection: 'row',
    gap: 8,
  },
  activityTypeButtonContainer: {
    flex: 1,
    alignItems: 'center',
    gap: 8,
  },
  activityTypeButtonWrapper: {
    width: 90,
    height: 60,
    position: 'relative',
  },
  activityTypeButton: {
    width: 80,
    height: 50,
    borderRadius: 12,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 5, // 5px gap from outline
    left: 5, // 5px gap from outline
    zIndex: 2, // Above the outline
  },
  activityTypeButtonOutline: {
    width: 90,
    height: 60,
    borderRadius: 16,
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: Colors.white, // White fill
    borderWidth: 3, // Thicker border
    zIndex: 1, // Below the filled rectangle
  },
  activityTypeButtonText: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  activityTypeButtonTextSelected: {
    color: Colors.black,
  },
  activityTypeButtonTextDefault: {
    color: Colors.grayText,
  },
  mentionInputContainer: {
    borderWidth: 1,
    borderRadius: 8,
    backgroundColor: Colors.white,
    padding: 8,
    minHeight: 100,
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'visible', // Allow suggestions to show
  },
});
